import { Box, Button, Divider, Paper, Stack, Typography } from '@mui/material';
import { useInfiniteQuery } from '@tanstack/react-query';

import { readContactHistory } from '../../api/business/endpoints';
import type { ContactField, ContactHistoryEntry } from '../../api/business/types';
import { ErrorNotice } from '../../components/ErrorNotice';
import { formatDateTime } from '../../components/format';
import { useLanguage } from '../../i18n/LanguageContext';

/** A value of the history as a person reads it: a kind, type or flag in words, anything else as stored. */
const useValueOf = () => {
  const { t } = useLanguage();

  return (field: ContactField, value: string | null): string => {
    if (value === null) {
      return t('common.none');
    }

    if (field === 'kind' && (value === 'ORGANISATION' || value === 'PERSON')) {
      return t(`contactKind.${value}` as const);
    }

    if (field === 'type' && ['CUSTOMER', 'SUPPLIER', 'PARTNER', 'OTHER'].includes(value)) {
      return t(`contactType.${value as 'CUSTOMER'}` as const);
    }

    if (field === 'doNotContact') {
      return value === 'true' ? t('contactHistory.yes') : t('contactHistory.no');
    }

    return value;
  };
};

/** A contact's history (R0039 D-23): every change, newest first, field by field — before and after. */
export const ContactHistory = ({ companyId, contactId }: { companyId: string; contactId: string }) => {
  const { language, t } = useLanguage();
  const valueOf = useValueOf();
  const history = useInfiniteQuery({
    queryKey: ['contact-history', companyId, contactId],
    queryFn: ({ pageParam }) => readContactHistory(companyId, contactId, pageParam),
    initialPageParam: 1,
    getNextPageParam: (last) => (last.page * last.pageSize < last.total ? last.page + 1 : undefined),
  });
  const entries: Array<ContactHistoryEntry> = (history.data?.pages ?? []).flatMap((page) => page.items);

  return (
    <Paper variant="outlined" sx={{ p: 2.5 }}>
      <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1.5 }}>
        {t('contact.section.history')}
      </Typography>
      <ErrorNotice error={history.error} onRetry={() => void history.refetch()} />
      {history.data && entries.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          {t('contactHistory.empty')}
        </Typography>
      ) : null}
      <Stack divider={<Divider flexItem />} spacing={1.5}>
        {entries.map((entry) => (
          <Box key={entry.id}>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              {t(`contactHistory.action.${entry.action}` as const)}
            </Typography>
            <Typography variant="caption" color="text.secondary" component="div" sx={{ mb: 0.5 }}>
              {formatDateTime(entry.createdAt, language)} · {entry.actorEmail ?? t('contact.someone')}
            </Typography>
            {entry.changes.map((change) => (
              <Box key={change.field} sx={{ mb: 0.75 }}>
                <Typography variant="caption" color="text.secondary">
                  {t(`contactHistory.field.${change.field}` as const)}
                </Typography>
                <Stack direction="row" spacing={1} alignItems="flex-start">
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{
                      whiteSpace: 'pre-wrap',
                      textDecoration: change.before ? 'line-through' : 'none',
                      flex: 1,
                      minWidth: 0,
                      overflowWrap: 'anywhere',
                    }}
                  >
                    {valueOf(change.field, change.before)}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    →
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{ whiteSpace: 'pre-wrap', flex: 1, minWidth: 0, overflowWrap: 'anywhere' }}
                  >
                    {valueOf(change.field, change.after)}
                  </Typography>
                </Stack>
              </Box>
            ))}
          </Box>
        ))}
      </Stack>
      {history.hasNextPage ? (
        <Button
          size="small"
          sx={{ mt: 1.5 }}
          disabled={history.isFetchingNextPage}
          onClick={() => void history.fetchNextPage()}
        >
          {t('contactHistory.more')}
        </Button>
      ) : null}
    </Paper>
  );
};
